import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Segmented,
  Slider,
  Spin,
  Upload,
  message,
} from "antd";
import {
  CheckOutlined,
  ClearOutlined,
  EyeOutlined,
  KeyOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SignatureOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { sessionEmployeeId } from "@/lib/signature/session";
import { orpcClient } from "@/lib/orpc-client";
import {
  DEFAULT_SENSITIVITY,
  SENSITIVITY_MAX,
  SENSITIVITY_MIN,
  loadImage,
  prepareSignature,
  readFileAsDataUrl,
  renderSignature,
  type InkColor,
  type Rotation,
  type SignatureSource,
} from "@/lib/signature/removeBackground";
import SignatureEraser from "./SignatureEraser";
import {
  getUnlockedSignature,
  setUnlockedSignature,
} from "@/lib/signature/cache";

export { clearUnlockedSignatures } from "@/lib/signature/cache";

export const SIGNATURE_PASSWORD_MIN = 8;
export const SIGNATURE_PASSWORD_MAX = 128;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

type SignatureStatus = "unknown" | "uploaded" | "notUploaded";

const checkerboard: React.CSSProperties = {
  backgroundColor: "#fff",
  backgroundImage:
    "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
  border: "1px dashed #d9d9d9",
  borderRadius: 8,
  minHeight: 120,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 8,
};

export function signatureErrorMessage(err: unknown, t: TFunction): string {
  const code = (err as { code?: string } | null)?.code;
  if (code === "SIGNATURE_PASSWORD_INCORRECT")
    return t("employee.signature.passwordIncorrect");
  if (code === "SIGNATURE_NOT_UPLOADED")
    return t("employee.signature.notUploaded");
  if (code === "FORBIDDEN") return t("employee.signature.forbidden");
  return t("employee.signature.requestFailed");
}

interface SignatureSectionProps {
  employeeId?: string;
  sectionTitleStyle: React.CSSProperties;
  disabled?: boolean;
}

function SignatureSection({
  employeeId,
  sectionTitleStyle,
  disabled,
}: SignatureSectionProps) {
  const { t } = useTranslation();
  const [selfId, setSelfId] = useState<string | null | undefined>(undefined);
  const [status, setStatus] = useState<SignatureStatus>("unknown");
  const [signature, setSignature] = useState<string | null>(null);

  const [viewPassword, setViewPassword] = useState("");
  const [viewing, setViewing] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [sourceImg, setSourceImg] = useState<HTMLImageElement | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [rotation, setRotation] = useState<Rotation>(0);
  const [prepared, setPrepared] = useState<SignatureSource | null>(null);
  const [sensitivity, setSensitivity] = useState(DEFAULT_SENSITIVITY);
  const [suggested, setSuggested] = useState(DEFAULT_SENSITIVITY);
  const [ink, setInk] = useState<InkColor>("original");
  const [processed, setProcessed] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [maskVersion, setMaskVersion] = useState(0);
  const eraseMask = useMemo(
    () => (prepared ? new Uint8Array(prepared.width * prepared.height) : null),
    [prepared],
  );
  const [uploadPassword, setUploadPassword] = useState("");
  const [uploadConfirm, setUploadConfirm] = useState("");
  const [uploading, setUploading] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newConfirm, setNewConfirm] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    let active = true;
    sessionEmployeeId().then((id) => {
      if (active) setSelfId(id);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const cached = employeeId ? getUnlockedSignature(employeeId) : undefined;
    setStatus(cached ? "uploaded" : "unknown");
    setSignature(cached ?? null);
    setViewPassword("");
  }, [employeeId]);

  const rememberSignature = (image: string) => {
    if (employeeId) setUnlockedSignature(employeeId, image);
    setSignature(image);
    setStatus("uploaded");
  };

  useEffect(() => {
    if (!prepared) {
      setProcessed(null);
      return;
    }
    setProcessing(true);
    const timer = window.setTimeout(() => {
      setProcessed(
        renderSignature(prepared, { sensitivity, ink, erased: eraseMask }),
      );
      setProcessing(false);
    }, 60);
    return () => window.clearTimeout(timer);
  }, [prepared, sensitivity, ink, eraseMask, maskVersion]);

  const rotate = (delta: 90 | -90) => {
    if (!sourceImg) return;
    const next = ((((rotation + delta) % 360) + 360) % 360) as Rotation;
    setRotation(next);
    setPrepared(prepareSignature(sourceImg, next));
  };

  const isSelf = !!employeeId && !!selfId && employeeId === selfId;
  const isFirstUpload = status !== "uploaded";

  const handleView = async () => {
    if (!viewPassword) return;
    setViewing(true);
    try {
      const result = await orpcClient.employeeSignature.read({
        password: viewPassword,
      });
      rememberSignature(result.image);
      setViewPassword("");
    } catch (err) {
      if ((err as { code?: string })?.code === "SIGNATURE_NOT_UPLOADED") {
        setStatus("notUploaded");
        setViewPassword("");
      }
      message.error(signatureErrorMessage(err, t));
    } finally {
      setViewing(false);
    }
  };

  const handlePickFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      message.error(t("employee.signature.invalidFile"));
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error(t("employee.signature.fileTooLarge"));
      return false;
    }
    try {
      const url = await readFileAsDataUrl(file);
      const img = await loadImage(url);
      const prep = prepareSignature(img, 0);
      setSourceUrl(url);
      setSourceImg(img);
      setRotation(0);
      setPrepared(prep);
      setSuggested(prep.suggestedSensitivity);
      setSensitivity(prep.suggestedSensitivity);
      setInk("original");
      setErasing(false);
      setUploadPassword("");
      setUploadConfirm("");
      setUploadOpen(true);
    } catch {
      message.error(t("employee.signature.invalidFile"));
    }
    return false;
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setSourceImg(null);
    setSourceUrl(null);
    setPrepared(null);
    setErasing(false);
    setUploadPassword("");
    setUploadConfirm("");
  };

  const uploadError = (() => {
    if (processing) return t("employee.signature.processing");
    if (!processed) return t("employee.signature.emptyResult");
    if (uploadPassword.length < SIGNATURE_PASSWORD_MIN)
      return t("employee.signature.passwordTooShort", {
        min: SIGNATURE_PASSWORD_MIN,
      });
    if (isFirstUpload && uploadPassword !== uploadConfirm)
      return t("employee.signature.passwordMismatch");
    return null;
  })();

  const handleUpload = async () => {
    if (uploadError || !processed) return;
    setUploading(true);
    try {
      await orpcClient.employeeSignature.upload({
        image: processed,
        password: uploadPassword,
      });
      rememberSignature(processed);
      message.success(t("employee.signature.uploadSuccess"));
      closeUpload();
    } catch (err) {
      if ((err as { code?: string })?.code === "SIGNATURE_PASSWORD_INCORRECT")
        setStatus("uploaded");
      message.error(signatureErrorMessage(err, t));
    } finally {
      setUploading(false);
    }
  };

  const changePwError = (() => {
    if (!oldPassword) return t("employee.signature.oldPasswordRequired");
    if (newPassword.length < SIGNATURE_PASSWORD_MIN)
      return t("employee.signature.passwordTooShort", {
        min: SIGNATURE_PASSWORD_MIN,
      });
    if (newPassword !== newConfirm)
      return t("employee.signature.passwordMismatch");
    return null;
  })();

  const closeChangePw = () => {
    setPwOpen(false);
    setOldPassword("");
    setNewPassword("");
    setNewConfirm("");
  };

  const handleChangePassword = async () => {
    if (changePwError) return;
    setChangingPw(true);
    try {
      await orpcClient.employeeSignature.changePassword({
        oldPassword,
        newPassword,
      });
      message.success(t("employee.signature.passwordChanged"));
      closeChangePw();
    } catch (err) {
      if ((err as { code?: string })?.code === "SIGNATURE_NOT_UPLOADED")
        setStatus("notUploaded");
      message.error(signatureErrorMessage(err, t));
    } finally {
      setChangingPw(false);
    }
  };

  const title = (
    <div style={sectionTitleStyle}>
      <SignatureOutlined />
      <span>{t("employee.signature.title")}</span>
    </div>
  );

  if (!isSelf) {
    return (
      <div data-testid="signature-section">
        {title}
        <p style={{ fontSize: 13, color: "#999", margin: 0 }}>
          {employeeId
            ? t("employee.signature.selfOnly")
            : t("employee.signature.addHint")}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="signature-section">
      {title}
      <div style={checkerboard}>
        {signature ? (
          <img
            src={signature}
            alt={t("employee.signature.title")}
            style={{ maxHeight: 140, maxWidth: "100%", objectFit: "contain" }}
          />
        ) : status === "notUploaded" ? (
          <span style={{ fontSize: 13, color: "#999" }}>
            {t("employee.signature.notUploaded")}
          </span>
        ) : (
          <div style={{ width: "100%", maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "#999", marginBottom: 8 }}>
              {t("employee.signature.locked")}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Input.Password
                autoComplete="new-password"
                name="signature-pin"
                aria-label={t("employee.signature.password")}
                placeholder={t("employee.signature.password")}
                value={viewPassword}
                maxLength={SIGNATURE_PASSWORD_MAX}
                disabled={disabled}
                onChange={(e) => setViewPassword(e.target.value)}
                onPressEnter={handleView}
              />
              <Button
                icon={<EyeOutlined />}
                disabled={disabled || !viewPassword}
                loading={viewing}
                onClick={handleView}
              >
                {t("employee.signature.view")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <p style={{ fontSize: 12, color: "#999", margin: "8px 0" }}>
        {t("employee.signature.hint")}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={handlePickFile}
          disabled={disabled}
        >
          <Button icon={<UploadOutlined />} disabled={disabled}>
            {status === "uploaded"
              ? t("employee.signature.replace")
              : t("employee.signature.upload")}
          </Button>
        </Upload>
        {status !== "notUploaded" && (
          <Button
            icon={<KeyOutlined />}
            disabled={disabled}
            onClick={() => setPwOpen(true)}
          >
            {t("employee.signature.changePassword")}
          </Button>
        )}
      </div>

      <Modal
        open={uploadOpen}
        title={
          status === "uploaded"
            ? t("employee.signature.replace")
            : t("employee.signature.upload")
        }
        onCancel={closeUpload}
        onOk={handleUpload}
        okText={t("employee.signature.save")}
        cancelText={t("employee.edit.cancel")}
        okButtonProps={{ disabled: !!uploadError, loading: uploading }}
        width={640}
        destroyOnClose
      >
        {erasing && prepared && eraseMask ? (
          <div style={{ marginBottom: 12 }}>
            <SignatureEraser
              source={prepared}
              sensitivity={sensitivity}
              ink={ink}
              mask={eraseMask}
              onCommit={() => setMaskVersion((v) => v + 1)}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4" style={{ marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>
                {t("employee.signature.original")}
              </div>
              <div style={{ ...checkerboard, background: "#fafafa" }}>
                {sourceUrl && (
                  <img
                    src={sourceUrl}
                    alt={t("employee.signature.original")}
                    style={{
                      maxHeight: 160,
                      maxWidth: rotation % 180 ? 160 : "100%",
                      transform: `rotate(${rotation}deg)`,
                    }}
                  />
                )}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>
                {t("employee.signature.processed")}
              </div>
              <div style={checkerboard}>
                {processing && !processed ? (
                  <Spin />
                ) : processed ? (
                  <img
                    data-testid="signature-processed"
                    src={processed}
                    alt={t("employee.signature.processed")}
                    style={{ maxHeight: 160, maxWidth: "100%" }}
                  />
                ) : (
                  <span style={{ fontSize: 13, color: "#cf1322" }}>
                    {t("employee.signature.emptyResult")}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <Button
            icon={<RotateLeftOutlined />}
            aria-label={t("employee.signature.rotateLeft")}
            disabled={erasing}
            onClick={() => rotate(-90)}
          />
          <Button
            icon={<RotateRightOutlined />}
            aria-label={t("employee.signature.rotateRight")}
            disabled={erasing}
            onClick={() => rotate(90)}
          />
          <Button
            type={erasing ? "primary" : "default"}
            icon={erasing ? <CheckOutlined /> : <ClearOutlined />}
            disabled={!prepared}
            onClick={() => setErasing((v) => !v)}
          >
            {erasing
              ? t("employee.signature.eraserDone")
              : t("employee.signature.eraser")}
          </Button>
          <span style={{ fontSize: 13, marginLeft: 8 }}>
            {t("employee.signature.inkColor")}
          </span>
          <Segmented
            value={ink}
            onChange={(v) => setInk(v as InkColor)}
            options={[
              { value: "original", label: t("employee.signature.inkOriginal") },
              { value: "black", label: t("employee.signature.inkBlack") },
              { value: "blue", label: t("employee.signature.inkBlue") },
            ]}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 13,
          }}
        >
          <label>{t("employee.signature.sensitivity")}</label>
          <Button
            type="link"
            size="small"
            disabled={sensitivity === suggested}
            onClick={() => setSensitivity(suggested)}
          >
            {t("employee.signature.resetSensitivity")}
          </Button>
        </div>
        <Slider
          min={SENSITIVITY_MIN}
          max={SENSITIVITY_MAX}
          value={sensitivity}
          onChange={(v) => setSensitivity(v as number)}
        />
        <p style={{ fontSize: 12, color: "#999", margin: "-4px 0 12px" }}>
          {t("employee.signature.sensitivityHint")}
        </p>
        <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
          {isFirstUpload
            ? t("employee.signature.newPassword")
            : t("employee.signature.currentPassword")}
        </label>
        <Input.Password
          autoComplete="new-password"
          name="signature-pin"
          aria-label={
            isFirstUpload
              ? t("employee.signature.newPassword")
              : t("employee.signature.currentPassword")
          }
          value={uploadPassword}
          maxLength={SIGNATURE_PASSWORD_MAX}
          onChange={(e) => setUploadPassword(e.target.value)}
        />
        {isFirstUpload && (
          <>
            <label
              style={{ display: "block", fontSize: 13, margin: "12px 0 4px" }}
            >
              {t("employee.signature.confirmPassword")}
            </label>
            <Input.Password
              autoComplete="new-password"
              name="signature-pin"
              aria-label={t("employee.signature.confirmPassword")}
              value={uploadConfirm}
              maxLength={SIGNATURE_PASSWORD_MAX}
              onChange={(e) => setUploadConfirm(e.target.value)}
            />
            <p style={{ fontSize: 12, color: "#999", margin: "8px 0 0" }}>
              {t("employee.signature.firstUploadHint")}
            </p>
          </>
        )}
        {uploadError && (uploadPassword || uploadConfirm) && (
          <p style={{ fontSize: 12, color: "#cf1322", margin: "8px 0 0" }}>
            {uploadError}
          </p>
        )}
      </Modal>

      <Modal
        open={pwOpen}
        title={t("employee.signature.changePassword")}
        onCancel={closeChangePw}
        onOk={handleChangePassword}
        okText={t("employee.signature.save")}
        cancelText={t("employee.edit.cancel")}
        okButtonProps={{ disabled: !!changePwError, loading: changingPw }}
        destroyOnClose
      >
        <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
          {t("employee.signature.currentPassword")}
        </label>
        <Input.Password
          autoComplete="new-password"
          name="signature-pin"
          aria-label={t("employee.signature.currentPassword")}
          value={oldPassword}
          maxLength={SIGNATURE_PASSWORD_MAX}
          onChange={(e) => setOldPassword(e.target.value)}
        />
        <label style={{ display: "block", fontSize: 13, margin: "12px 0 4px" }}>
          {t("employee.signature.newPassword")}
        </label>
        <Input.Password
          autoComplete="new-password"
          name="signature-pin"
          aria-label={t("employee.signature.newPassword")}
          value={newPassword}
          maxLength={SIGNATURE_PASSWORD_MAX}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <label style={{ display: "block", fontSize: 13, margin: "12px 0 4px" }}>
          {t("employee.signature.confirmPassword")}
        </label>
        <Input.Password
          autoComplete="new-password"
          name="signature-pin"
          aria-label={t("employee.signature.confirmPassword")}
          value={newConfirm}
          maxLength={SIGNATURE_PASSWORD_MAX}
          onChange={(e) => setNewConfirm(e.target.value)}
        />
        {changePwError && (oldPassword || newPassword || newConfirm) && (
          <p style={{ fontSize: 12, color: "#cf1322", margin: "8px 0 0" }}>
            {changePwError}
          </p>
        )}
      </Modal>
    </div>
  );
}

export default SignatureSection;
